import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ShieldCheck, FileText, Download, Image as ImageIcon, Package } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const formaterDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso.endsWith('Z') ? iso : iso + 'Z');
  if (isNaN(d)) return '';
  return d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const FicheDocuments = ({ prospectId, role }) => {
  const [chantier, setChantier] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [enCours, setEnCours] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/chantiers/par-prospect/${prospectId}`)
      .then((r) => setChantier(r.data.data))
      .catch(() => setChantier(null))
      .finally(() => setChargement(false));
  }, [prospectId]);

  const ouvrirPdf = async (docId) => {
    try {
      const r = await axios.get(`${API}/api/documents/${chantier.id}/fichier/${docId}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert("Impossible d'ouvrir le document");
    }
  };

  const telechargerDossier = async () => {
    setEnCours(true);
    try {
      const r = await axios.get(`${API}/api/documents/${chantier.id}/dossier`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossier-${chantier.client?.nom || 'client'}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      alert('Impossible de générer le dossier');
    } finally {
      setEnCours(false);
    }
  };

  const carte = { background: '#fff', borderRadius: '12px', padding: '18px', border: '1px solid #E5E7EB', marginBottom: '16px' };

  if (chargement) return <div style={{ padding: '32px', textAlign: 'center', color: '#9CA3AF' }}>Chargement…</div>;

  if (!chantier) return (
    <div style={{ padding: '32px', textAlign: 'center', color: '#6B7280' }}>
      Aucun chantier pour ce client
    </div>
  );

  const documents = chantier.documents || [];
  const photos = chantier.photos || [];

  return (
    <div style={{ padding: '4px' }}>
      <div style={carte}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Dossier client</div>
            <div style={{ fontSize: '13px', color: '#6B7280' }}>
              {documents.length} document{documents.length > 1 ? 's' : ''} · {photos.length} photo{photos.length > 1 ? 's' : ''}
            </div>
          </div>
          <button onClick={telechargerDossier} disabled={enCours || (documents.length === 0 && photos.length === 0)}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px', padding: '11px 16px', border: 'none', borderRadius: '9px',
              background: (documents.length === 0 && photos.length === 0) ? '#F3F4F6' : '#10B981',
              color: (documents.length === 0 && photos.length === 0) ? '#9CA3AF' : '#fff',
              fontSize: '14px', cursor: (documents.length === 0 && photos.length === 0) ? 'default' : 'pointer'
            }}>
            <Package size={16} /> {enCours ? 'Préparation…' : 'Télécharger le dossier'}
          </button>
        </div>
      </div>

      <div style={carte}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>Documents signés</div>
        {documents.length === 0 ? (
          <div style={{ fontSize: '14px', color: '#9CA3AF' }}>Aucun document signé pour l'instant</div>
        ) : documents.map((doc, i) => (
          <div key={doc.id} style={{ paddingTop: i > 0 ? '16px' : 0, marginTop: i > 0 ? '16px' : 0, borderTop: i > 0 ? '1px solid #F3F4F6' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '7px' }}>
              <ShieldCheck size={17} color="#10B981" />
              <span style={{ fontSize: '14px', fontWeight: 500 }}>{doc.titre}</span>
            </div>
            <div style={{ fontSize: '13px', color: '#6B7280', marginBottom: '3px' }}>Signé par {doc.signe_par}</div>
            <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginBottom: '3px' }}>
              {formaterDate(doc.horodatage)}{doc.technicien ? ` · ${doc.technicien}` : ''}
            </div>
            <div style={{ fontSize: '10.5px', color: '#D1D5DB', fontFamily: 'monospace', marginBottom: '11px' }}>
              {doc.empreinte_sha256 ? doc.empreinte_sha256.slice(0, 32) + '…' : ''}
            </div>
            <button onClick={() => ouvrirPdf(doc.id)}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '13px', color: '#374151', background: '#fff', cursor: 'pointer' }}>
              <FileText size={14} /> Ouvrir le PDF
            </button>
          </div>
        ))}
      </div>

      <div style={carte}>
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '10px' }}>Photos du chantier</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '14px', color: photos.length ? '#374151' : '#9CA3AF' }}>
          <ImageIcon size={16} color="#9CA3AF" />
          {photos.length ? `${photos.length} photo${photos.length > 1 ? 's' : ''} · incluses dans le dossier` : 'Aucune photo'}
        </div>
      </div>

      <div style={{ ...carte, background: '#F9FAFB' }}>
        <div style={{ fontSize: '13px', color: '#9CA3AF', lineHeight: 1.6 }}>
          <strong style={{ color: '#6B7280' }}>Bientôt</strong> · devis, facture, fiche micro-onduleurs et accès à l'application APSystems
        </div>
      </div>
    </div>
  );
};

export default FicheDocuments;
